import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Send, MapPin, Smile, Paperclip, Phone, Video } from 'lucide-react'
import { useDemo } from '../contexts/DemoContext'
import toast from 'react-hot-toast'

const Chat = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'Maria Silva',
      content: 'Oi pessoal! Como estão?',
      timestamp: new Date(Date.now() - 3600000),
      type: 'text'
    },
    {
      id: 2,
      sender: 'João Silva',
      content: 'Tudo bem! Chegando em casa agora.',
      timestamp: new Date(Date.now() - 1800000),
      type: 'text'
    },
    {
      id: 3,
      sender: 'Ana Silva',
      content: 'Compartilhei minha localização',
      timestamp: new Date(Date.now() - 900000),
      type: 'location',
      location: { lat: -23.5505, lng: -46.6333, address: 'São Paulo, SP' }
    }
  ])
  
  const [newMessage, setNewMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef(null)
  const { user, family } = useDemo()

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSendMessage = (e) => {
    e.preventDefault()
    if (!newMessage.trim()) return

    const message = {
      id: Date.now(),
      sender: user?.name || 'Você',
      content: newMessage,
      timestamp: new Date(),
      type: 'text'
    }

    setMessages(prev => [...prev, message])
    setNewMessage('')
    toast.success('Mensagem enviada!')
  }

  const handleShareLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const message = {
            id: Date.now(),
            sender: user?.name || 'Você',
            content: 'Compartilhei minha localização',
            timestamp: new Date(),
            type: 'location',
            location: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              address: 'Localização atual'
            }
          }
          setMessages(prev => [...prev, message])
          toast.success('Localização compartilhada!')
        },
        () => {
          toast.error('Erro ao obter localização')
        }
      )
    } else {
      toast.error('Geolocalização não suportada')
    }
  }

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Chat Familiar</h1>
              <p className="text-gray-600">
                {family?.members?.length || 0} membros online
              </p>
            </div>
            <div className="flex space-x-2">
              <button className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
                <Phone className="w-5 h-5" />
              </button>
              <button className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                <Video className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Messages */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-lg shadow-lg mb-6 h-96 overflow-y-auto"
        >
          <div className="p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.sender === (user?.name || 'Você') ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                    message.sender === (user?.name || 'Você')
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-800'
                  }`}
                >
                  <div className="text-xs opacity-75 mb-1">{message.sender}</div>
                  {message.type === 'location' ? (
                    <div>
                      <div className="flex items-center space-x-1 mb-1">
                        <MapPin className="w-4 h-4" />
                        <span>Localização</span>
                      </div>
                      <div className="text-xs opacity-75">
                        {message.location?.address}
                      </div>
                    </div>
                  ) : (
                    <div>{message.content}</div>
                  )}
                  <div className="text-xs opacity-75 mt-1">
                    {formatTime(message.timestamp)}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </motion.div>

        {/* Message Input */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-4"
        >
          <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleShareLocation}
              className="p-2 text-gray-500 hover:text-blue-500 transition-colors"
            >
              <MapPin className="w-5 h-5" />
            </button>
            <button
              type="button"
              className="p-2 text-gray-500 hover:text-blue-500 transition-colors"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Digite sua mensagem..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              className="p-2 text-gray-500 hover:text-blue-500 transition-colors"
            >
              <Smile className="w-5 h-5" />
            </button>
            <button
              type="submit"
              disabled={!newMessage.trim()}
              className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  )
}

export default Chat